import axios, { type AxiosError, type AxiosInstance } from 'axios';
import { SESSION_BACKGROUND_HEADER } from '@simsm/shared-types';
import { hasHeldSession } from './session-presence';
import { sentWithoutInput } from './person-activity';

export type NormalizedApiError = {
  status: number;
  code: string;
  message: string;
  requestId?: string;
  /**
   * `409 SITE_HAS_DRAFT` names the draft that already holds the location, so
   * the report can open that one rather than move rows onto it (spec 054 T063).
   */
  draftId?: string;
  /**
   * `409 MEDICINE_CODE_TAKEN` names the medicine that already holds the code, so the form can
   * tell the administrator which one it is (UC20-08, TC-UC20-08-02).
   */
  existingMedicineLabel?: string;
};

export function isNormalizedApiError(value: unknown): value is NormalizedApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    'status' in value &&
    'code' in value &&
    'message' in value
  );
}

// Every code the administration interface can provoke, mapped to the Tolgee key
// that explains it in the reader's language. Enumerated, not estimated:
// `specs/002-admin-interface-repairs/contracts/error-codes.md` records all 162
// codes the six services throw across 252 sites, the route each reaches the
// browser through, and why the 28 that are missing here cannot be reached from
// an administration screen.
//
// This is the only code-to-copy table in the frontoffice. Four predated it —
// `decisionErrorKey` for correction decisions, `messageFor` for storage sites,
// `RegistrarFormPage`'s inline IDNO mapping and `mapConfirmError` for draft
// confirmation — and all four are gone. The last two are why the claim was worth
// re-checking rather than repeating: both read the code off
// `e.response.data.code`, a shape the response interceptor never produces (it
// rejects with a NormalizedApiError), so neither could ever match and both
// screens printed their fallback to every refusal they were written to explain.
//
// Reading a code outside this file is not automatically a fifth table: one
// screen still tests for a single code (`CrossIMSPStockPage`, NOT_IMSP_REGISTRAR)
// to choose a screen *state* rather than a sentence, which is a different job.
// What may not come back is a switch that turns codes into copy.
//
// Twelve codes keep the off-pattern `corrections.*`, `sites.*` and
// `stock_reporting.*` keys those tables already shipped, rather than being
// renamed into `api.error.*`: a second key for one code would be the same defect
// as a second table. Anything that needs a code-to-copy mapping maps it here.
const API_ERROR_KEYS: Record<string, string> = {
  // Session and role — the shell, reachable from every screen
  SESSION_MISSING: 'api.error.session_missing',
  SESSION_INVALID: 'api.error.session_invalid',
  SESSION_REQUIRED: 'api.error.session_required',
  SESSION_NOT_FOUND: 'api.error.session_not_found',
  ROLE_NOT_AVAILABLE: 'api.error.role_not_available',
  ROLE_NOT_DEFINED: 'api.error.role_not_defined',
  // The central authorization policy's refusals (docs/shape-ops-authorization-policy.md §4)
  FORBIDDEN_ROLE: 'api.error.forbidden_role',
  ACTIVE_REGISTRAR_REQUIRED: 'api.error.active_registrar_required',
  ASSOCIATED_SITES_REQUIRED: 'api.error.associated_sites_required',
  REGISTRAR_OUT_OF_SCOPE: 'api.error.registrar_out_of_scope',
  SITE_OUT_OF_SCOPE: 'api.error.site_out_of_scope',
  TARGET_INVALID: 'api.error.target_invalid',
  POLICY_UNDECLARED: 'api.error.policy_undeclared',
  POLICY_NOT_EVALUATED: 'api.error.policy_undeclared',
  VALIDATION_FAILED: 'api.error.validation_failed',

  // Medicines and their codes (UC20)
  MEDICINE_NOT_FOUND: 'api.error.medicine_not_found',
  MEDICINE_NO_CHANGES: 'api.error.medicine_no_changes',
  MEDICINE_PARTIAL_UNSUPPORTED: 'api.error.medicine_partial_unsupported',
  MEDICINE_CODE_NOT_FOUND: 'api.error.medicine_code_not_found',
  MEDICINE_CODE_REQUIRED: 'api.error.medicine_code_required',
  MEDICINE_CODE_NO_CHANGES: 'api.error.medicine_code_no_changes',
  MEDICINE_CODE_TAKEN: 'api.error.medicine_code_taken',
  MEDICINE_ALREADY_EXISTS: 'api.error.medicine_already_exists',
  INVALID_MEDICINE_CODE_KIND: 'api.error.invalid_medicine_code_kind',
  INVALID_MEDICINE_CODE_SOURCE: 'api.error.invalid_medicine_code_source',
  INVALID_AUTHORIZATION_STATUS: 'api.error.invalid_authorization_status',
  NOMENCLATOR_TYPE_INVALID: 'api.error.nomenclator_type_invalid',
  STALE_ROW_VERSION: 'api.error.stale_row_version',
  FIELD_REQUIRED: 'api.error.field_required',

  // Geography (UC22)
  GEOGRAPHY_NOT_FOUND: 'api.error.geography_not_found',
  GEOGRAPHY_INACTIVE: 'api.error.geography_inactive',
  // UC30-05: a site sits in a locality, never in a node with subdivisions.
  GEOGRAPHY_NOT_LOCALITY: 'api.error.geography_not_locality',
  GEOGRAPHY_ID_REQUIRED: 'api.error.geography_id_required',
  GEOGRAPHY_ID_INVALID: 'api.error.geography_id_invalid',
  GEOGRAPHY_PARENT_IS_A_MOVE: 'api.error.geography_parent_is_a_move',
  GEOGRAPHY_PARTIAL_UNSUPPORTED: 'api.error.geography_partial_unsupported',
  INVALID_GEOGRAPHY_KIND: 'api.error.invalid_geography_kind',
  PARENT_NOT_FOUND: 'api.error.parent_not_found',
  CANNOT_MOVE_ROOT: 'api.error.cannot_move_root',
  CYCLE_FORBIDDEN: 'api.error.cycle_forbidden',

  // Imports shared by the geography and medicine importers
  FILE_REQUIRED: 'api.error.file_required',
  UNSUPPORTED_FORMAT: 'api.error.unsupported_format',

  // Storage sites (UC21, UC30)
  SITE_NOT_FOUND: 'api.error.site_not_found',
  SITE_FIELDS_REQUIRED: 'api.error.site_fields_required',
  SITE_NAME_REQUIRED: 'api.error.site_name_required',
  SITE_NO_CHANGES: 'api.error.site_no_changes',
  SITE_CODE_TAKEN: 'sites.error.code_taken_server',
  SITE_NAME_TAKEN: 'sites.error.name_taken_server',
  SITE_GEOGRAPHY_REQUIRED: 'sites.error.locality_required',
  INTERNAL_CODE_REQUIRED: 'api.error.internal_code_required',
  INTERNAL_CODE_INVALID: 'api.error.internal_code_invalid',
  // UC30-04, CF-08: a new site's code is {IDNO}NNNN of its own Registrar.
  SITE_CODE_FORMAT_INVALID: 'api.error.site_code_format_invalid',
  SITE_CODE_PREFIX_MISMATCH: 'api.error.site_code_prefix_mismatch',
  NAME_REQUIRED: 'api.error.name_required',
  NAME_INVALID: 'api.error.name_invalid',
  TIP_REQUIRED: 'api.error.tip_required',
  TIP_INVALID: 'api.error.tip_invalid',
  ADRESA_INVALID: 'api.error.adresa_invalid',
  MPASS_ORG_CODE_INVALID: 'api.error.mpass_org_code_invalid',
  MPASS_ORG_CODE_TAKEN: 'api.error.mpass_org_code_taken',
  // CF-48: each administrator lifts its own block only (UC30-06, UC42-01).
  SITE_BLOCKED_BY_SYSTEM_ADMIN: 'api.error.site_blocked_by_system_admin',
  SITE_BLOCKED_BY_REGISTRAR: 'api.error.site_blocked_by_registrar',
  // CF-48 on the Sistem Registrator credentials (UC21-08, UC31-06).
  CREDENTIAL_BLOCKED_BY_SYSTEM_ADMIN: 'api.error.credential_blocked_by_system_admin',
  CREDENTIAL_BLOCKED_BY_REGISTRAR: 'api.error.credential_blocked_by_registrar',
  // CF-48 on one admitted address (UC21-08, UC31-06).
  IP_BLOCKED_BY_SYSTEM_ADMIN: 'api.error.ip_blocked_by_system_admin',
  IP_BLOCKED_BY_REGISTRAR: 'api.error.ip_blocked_by_registrar',
  // caiet UC21-03/UC21-04: RSUD does not know this IDNO, versus RSUD could not
  // be reached at all. One is a typo to correct, the other an outage to wait
  // out, and a single "not found" would have the administrator hunt the wrong one.
  RSUD_ENTITY_NOT_FOUND: 'api.error.rsud_entity_not_found',
  RSUD_UNAVAILABLE: 'api.error.rsud_unavailable',

  // Registrars (UC21)
  REGISTRAR_NOT_FOUND: 'api.error.registrar_not_found',
  REGISTRAR_REQUIRED: 'api.error.registrar_required',
  REGISTRAR_ID_REQUIRED: 'api.error.registrar_id_required',
  REGISTRAR_INACTIVE: 'api.error.registrar_inactive',
  INVALID_REGISTRAR_KIND: 'api.error.invalid_registrar_kind',
  IDNO_TAKEN: 'api.error.idno_taken',
  INVALID_IDNO: 'api.error.invalid_idno',

  // Nomenclator and geography history (UC20-12)
  HISTORY_ENTITY_KIND_UNKNOWN: 'api.error.history_entity_kind_unknown',
  HISTORY_RANGE_INVALID: 'api.error.history_range_invalid',
  HISTORY_CURSOR_INVALID: 'api.error.history_cursor_invalid',

  // Aggregates (UC-DASH)
  AGGREGATE_NOT_FOUND: 'api.error.aggregate_not_found',
  AGGREGATE_EXISTS: 'api.error.aggregate_exists',
  AGGREGATE_IN_USE: 'api.error.aggregate_in_use',
  AGGREGATE_FIELDS_REQUIRED: 'api.error.aggregate_fields_required',
  AGGREGATE_PARTIAL_UNSUPPORTED: 'api.error.aggregate_partial_unsupported',
  AGGREGATE_PREDEFINED: 'api.error.aggregate_predefined',
  BASE_AGGREGATE_REQUIRED: 'api.error.base_aggregate_required',
  GROUP_BY_REQUIRED: 'api.error.group_by_required',
  CALCULATIONS_REQUIRED: 'api.error.calculations_required',
  SPEC_REQUIRED: 'api.error.spec_required',
  INVALID_REFRESH_RANGE: 'api.error.invalid_refresh_range',

  // Dashboards and their role grants (UC-DASH)
  DASHBOARD_NOT_FOUND: 'api.error.dashboard_not_found',
  DASHBOARD_TITLE_REQUIRED: 'api.error.dashboard_title_required',
  DASHBOARD_SLUG_INVALID: 'api.error.dashboard_slug_invalid',
  DASHBOARD_SLUG_TAKEN: 'api.error.dashboard_slug_taken',
  DASHBOARD_ROLE_INVALID: 'api.error.dashboard_role_invalid',
  DASHBOARD_GRAFANA_UID_INVALID: 'api.error.dashboard_grafana_uid_invalid',
  DASHBOARD_VERSION_INVALID: 'api.error.dashboard_version_invalid',
  DASHBOARD_VERSION_NOT_FOUND: 'api.error.dashboard_version_not_found',
  DASHBOARD_SCOPE_CANNOT_EMPTY: 'api.error.dashboard_scope_cannot_empty',
  DASHBOARD_SCOPE_SHARED_WITH_ALL: 'api.error.dashboard_scope_shared_with_all',
  DASHBOARD_GRANT_INHERITED: 'api.error.dashboard_grant_inherited',
  DASHBOARD_DEFAULT_RANGE_INVALID: 'api.error.dashboard_default_range_invalid',
  DASHBOARD_MAX_RANGE_INVALID: 'api.error.dashboard_max_range_invalid',
  DASHBOARD_DEFAULT_RANGE_EXCEEDS_MAX: 'api.error.dashboard_default_range_exceeds_max',
  DASHBOARD_MAX_LOOKBACK_INVALID: 'api.error.dashboard_max_lookback_invalid',
  DASHBOARD_DEFAULT_RANGE_EXCEEDS_LOOKBACK: 'api.error.dashboard_default_range_exceeds_lookback',
  IMPORT_FILE_REQUIRED: 'api.error.import_file_required',
  IMPORT_FILE_NOT_JSON: 'api.error.import_file_not_json',
  IMPORT_NO_DASHBOARDS: 'api.error.import_no_dashboards',
  GRAFANA_UID_INVALID: 'api.error.grafana_uid_invalid',
  GRAFANA_DASHBOARD_NOT_FOUND: 'api.error.grafana_dashboard_not_found',
  GRAFANA_UNAVAILABLE: 'api.error.grafana_unavailable',

  // Observer roles (UC15, UC-DASH)
  OBSERVER_ROLE_NOT_FOUND: 'api.error.observer_role_not_found',
  OBSERVER_ROLE_EXISTS: 'api.error.observer_role_exists',
  OBSERVER_ROLE_CODE_INVALID: 'api.error.observer_role_code_invalid',
  OBSERVER_ROLE_FIELDS_REQUIRED: 'api.error.observer_role_fields_required',
  OBSERVER_ROLE_PARTIAL_UNSUPPORTED: 'api.error.observer_role_partial_unsupported',

  // Alert rules, templates, policies, channels and the trigger inbox (UC-ALRT)
  ALERT_RULE_NOT_FOUND: 'api.error.alert_rule_not_found',
  ALERT_RULE_EXISTS: 'api.error.alert_rule_exists',
  ALERT_RULE_NAME_INVALID: 'api.error.alert_rule_name_invalid',
  ALERT_RULE_FIELDS_REQUIRED: 'api.error.alert_rule_fields_required',
  ALERT_RULE_PARTIAL_UNSUPPORTED: 'api.error.alert_rule_partial_unsupported',
  ALERT_EVENT_NOT_FOUND: 'api.error.alert_event_not_found',
  YAML_REQUIRED: 'api.error.yaml_required',
  TEMPLATE_NOT_FOUND: 'api.error.template_not_found',
  TEMPLATE_NAME_INVALID: 'api.error.template_name_invalid',
  TEMPLATE_NAME_EXISTS: 'api.error.template_name_exists',
  TEMPLATE_FIELDS_REQUIRED: 'api.error.template_fields_required',
  TEMPLATE_NO_CHANGES: 'api.error.template_no_changes',
  TEMPLATE_IN_USE: 'api.error.template_in_use',
  POLICY_NOT_FOUND: 'api.error.policy_not_found',
  POLICY_NAME_INVALID: 'api.error.policy_name_invalid',
  POLICY_NAME_EXISTS: 'api.error.policy_name_exists',
  POLICY_NAME_REQUIRED: 'api.error.policy_name_required',
  POLICY_PARTIAL_UNSUPPORTED: 'api.error.policy_partial_unsupported',
  POLICY_IN_USE: 'api.error.policy_in_use',
  CHANNEL_NOT_FOUND: 'api.error.channel_not_found',
  CHANNEL_NAME_INVALID: 'api.error.channel_name_invalid',
  CHANNEL_NAME_EXISTS: 'api.error.channel_name_exists',
  CHANNEL_FIELDS_REQUIRED: 'api.error.channel_fields_required',
  CHANNEL_PARTIAL_UNSUPPORTED: 'api.error.channel_partial_unsupported',
  CHANNEL_RECIPIENT_REQUIRED: 'api.error.channel_recipient_required',
  CHANNEL_RECIPIENT_UNSUPPORTED: 'api.error.channel_recipient_unsupported',
  CHANNEL_URL_REQUIRED: 'api.error.channel_url_required',
  CHANNEL_ROUTE_LABEL_REQUIRED: 'api.error.channel_route_label_required',
  CHANNEL_IN_USE: 'api.error.channel_in_use',
  // The watched medicine group A4, A5 and A10 refer to (specs/060-agreed-alerts, plan.md C4, C5).
  WATCHED_GROUP_NOT_FOUND: 'api.error.watched_group_not_found',
  WATCHED_GROUP_IN_USE: 'api.error.watched_group_in_use',
  WATCHED_GROUP_THRESHOLD_REQUIRED: 'api.error.watched_group_threshold_required',
  WATCHED_GROUP_NAME_TAKEN: 'api.error.watched_group_name_exists',
  WATCHED_GROUP_MEMBER_EXISTS: 'api.error.watched_group_member_exists',
  INVALID_PAYLOAD: 'api.error.invalid_payload',

  // Registrar API credentials (UC15)
  CLIENT_NOT_FOUND: 'api.error.client_not_found',
  CLIENT_ID_TAKEN: 'api.error.client_id_taken',
  CLIENT_ID_INVALID: 'api.error.client_id_invalid',
  CLIENT_ID_RESERVED: 'api.error.client_id_reserved',
  PLAINTEXT_SECRET_REJECTED: 'api.error.plaintext_secret_rejected',
  SECRET_HASH_INVALID: 'api.error.secret_hash_invalid',
  ALLOWLIST_FIELDS_REQUIRED: 'api.error.allowlist_fields_required',
  ADDRESS_REQUIRED: 'api.error.address_required',
  IP_NOT_ADMITTED: 'api.error.ip_not_admitted',
  NATIONAL_CREDENTIAL_HAS_NO_REGISTRAR: 'api.error.national_credential_has_no_registrar',

  // System configuration (UC40)
  CRON_EXPRESSION_REQUIRED: 'api.error.cron_expression_required',
  TIMEOUT_SECONDS_REQUIRED: 'api.error.timeout_seconds_required',

  // The runtime system settings (UC20-10, UC40-06, SEC-08, UC15-05). The screen
  // refuses an out-of-range value before it sends anything, so reaching either
  // of these two means the screen's bounds and the enforcing service's have
  // drifted — which is exactly when a reader is owed the sentence rather than
  // "saving failed, try again": one is raised by the bff against its own
  // declaration, the other by the owning service against its stored row, and
  // they are one refusal to a reader, so they share one key.
  SYSTEM_CONFIG_VALUE_OUT_OF_RANGE: 'api.error.system_config_value_out_of_range',
  SYSTEM_SETTING_OUT_OF_RANGE: 'api.error.system_config_value_out_of_range',
  // The screen sent a parameter nobody on this screen owns. Not reachable by
  // using it — the keys come from the response — so it means the page is older
  // than the API, and reloading is the act that fixes it.
  SYSTEM_CONFIG_KEY_UNKNOWN: 'api.error.system_config_key_unknown',

  // Stock report drafts (UC50) — the keys the retired mapConfirmError shipped.
  // Thrown by core-service's draft.service.ts and carried across gRPC by
  // `grpcErrorToHttpException`, which is what preserves the domain code; a 404
  // from the same route carries none, so the confirm banner reads the status for
  // that one (StockReportingPage).
  VERSION_CONFLICT: 'stock_reporting.error.version_conflict.body',
  DRAFT_EMPTY: 'stock_reporting.error.draft_empty.body',
  DRAFT_HAS_ERRORS: 'stock_reporting.error.draft_has_errors.body',
  // Spec 054 (contracts/bff-stock-reporting.md §Error codes). The search refuses a
  // query under two characters, and a PATCH refuses to bind a location that
  // already has a draft rather than moving rows onto it.
  QUERY_TOO_SHORT: 'stock_reporting.search.error.query_too_short',
  SITE_HAS_DRAFT: 'stock_reporting.error.site_has_draft.body',
  // The bff's role gate on stock reporting (R10): a session whose active role is
  // not a registrar role, even with a registrar still in scope after a switch.
  REPORTER_ROLE_REQUIRED: 'stock_reporting.error.reporter_role_required',
  // The row codes spec 054 adds to core's validateRow. They reach the browser on
  // a row's `validationErrors`, not as a refused request, and they share their
  // keys with movement-form.ts, so the dialog and the report table say the same
  // sentence about the same rule.
  LOT_TOO_LONG: 'stock_reporting.movement.error.lot_too_long',
  QUANTITY_NEGATIVE: 'stock_reporting.movement.error.quantity',
  QUANTITY_UNCHANGED: 'stock_reporting.movement.error.no_difference',
  REASON_DIRECTION: 'stock_reporting.movement.error.reason_direction',
  // UC50-15, CF-15: a reason the Registrar's type does not allow, which core refuses
  // per row on save, on read and at confirm.
  REASON_NOT_ALLOWED: 'stock_reporting.movement.error.reason_not_allowed',
  // A refused request, not a row's error: core could read the Registrar's type from
  // neither catalog nor its published copy, and stored nothing, so a retry is safe
  // (docs/shape-ops-contract-codes.md §Decision 8).
  REGISTRAR_KIND_UNAVAILABLE: 'stock_reporting.error.registrar_kind_unavailable',
  // T026: a new report is judged against its Registrar and its location in core
  // (docs/shape-ops-report-scope.md §2.4-§2.6). The location is one the caller may
  // not report for, inactive, or not chosen; or catalog could not say, and nothing
  // was stored. REGISTRAR_INACTIVE at confirmation reads its own sentence
  // (StockReportingPage), since the key above speaks of the credentials.
  SITE_NOT_IN_SCOPE: 'stock_reporting.error.site_not_in_scope.body',
  SITE_INACTIVE: 'stock_reporting.error.site_inactive.body',
  SITE_REQUIRED: 'stock_reporting.save.needs_location.body',
  REGISTRAR_STATUS_UNAVAILABLE: 'stock_reporting.error.scope_unavailable.body',
  SITE_STATUS_UNAVAILABLE: 'stock_reporting.error.scope_unavailable.body',
  DUPLICATE_ROW: 'stock_reporting.movement.error.duplicate_row',
  CORRECTION_MISMATCH: 'stock_reporting.movement.error.correction_mismatch',
  // UC50-04, CF-02: the row's expiry is not the lot's. The sentence names the
  // lot's date, `{expiry}`, which row-messages.ts fills from the row's
  // knownExpiry (docs/shape-ops-existing-lot-alerts.md §Decision 3).
  EXPIRY_MISMATCH: 'stock_reporting.movement.error.expiry_mismatch',
  // UC50-16, CF-57: a manual row names no stock date; only a crafted request
  // sends one, and core refuses that row on save, on read and at confirm.
  STOCK_DATE_NOT_ALLOWED: 'stock_reporting.movement.error.stock_date_not_allowed',
  // An imported cell core could not read (R11): its row code when the import's
  // code is one, else SOURCE_VALUE_INVALID. The row's `sourceIssues` carry the
  // file's own text, which the report shows after this sentence.
  NEGATIVE_QUANTITY: 'stock_reporting.movement.error.source_negative_quantity',
  SOURCE_VALUE_INVALID: 'stock_reporting.movement.error.source_value_invalid',
  // The rest of core's row codes (UC50-09, TC-UC50-09-02): core's own sentence is
  // English for the API caller, so a row code without a key here would reach the
  // screen untranslated (docs/shape-ops-row-validation.md §Decision 4). A rule the
  // dialog also checks takes the dialog's key. MEDICINE_REQUIRED and
  // REASON_REQUIRED mean something else on the availability screen, so they are
  // keyed by their row field in row-messages.ts instead.
  MEDICINE_CODE_INACTIVE: 'stock_reporting.movement.error.medicine_inactive',
  MEDICINE_UNKNOWN: 'stock_reporting.movement.error.medicine_unknown',
  // CF-73 (3): a position AMDM has no medicine code for, on any lot.
  MEDICINE_NOT_REPORTABLE: 'stock_reporting.movement.error.medicine_not_reportable',
  LOT_REQUIRED: 'stock_reporting.movement.error.lot_required',
  EXPIRY_REQUIRED: 'stock_reporting.movement.error.expiry_required',
  QUANTITY_REQUIRED: 'stock_reporting.movement.error.quantity',
  QUANTITY_NON_INTEGER: 'stock_reporting.movement.error.quantity',
  QUANTITY_OUT_OF_RANGE: 'stock_reporting.movement.error.quantity_out_of_range',
  REASON_UNKNOWN: 'stock_reporting.movement.error.reason_unknown',
  COMMENT_REQUIRED: 'stock_reporting.movement.error.comment_required',
  COMMENT_TOO_LONG: 'stock_reporting.movement.error.comment_too_long',
  CORRECTION_TARGET_MISSING: 'stock_reporting.movement.error.correction_target_missing',
  // The draft's default reasons (R4), which the page sends after an add and from
  // "Motive implicite": core's UpsertDraft refuses a default that does not fit its
  // direction, a code it does not know, one the Registrar's type does not allow,
  // and a direction that is neither (docs/shape-ops-default-reasons.md §Decision 1).
  DEFAULT_REASON_DIRECTION: 'stock_reporting.error.default_reason_direction',
  DEFAULT_REASON_UNKNOWN: 'stock_reporting.error.default_reason_unknown',
  DEFAULT_REASON_NOT_ALLOWED: 'stock_reporting.error.default_reason_not_allowed',
  DEFAULT_DIRECTION_UNKNOWN: 'stock_reporting.error.default_direction_unknown',
  // POST /stock/drafts starts a draft only with its first row (FR-023). The lot
  // list's MEDICINE_CODE_REQUIRED is the catalog code above, one key for one code.
  DRAFT_ROWS_REQUIRED: 'stock_reporting.error.draft_rows_required',

  // Infrastructure, not domain: the two codes the platform mints when no service
  // got far enough to refuse anything.
  //
  // DATABASE_UNAVAILABLE is what a service now sends when its pool cannot hand
  // out a connection inside DATABASE_CONNECTION_TIMEOUT_MS
  // (libs/shared/database/src/lib/database-failure.ts). Before that timeout
  // existed the acquisition simply waited — 46.5s on 2026-09-01, past this
  // client's own 30s — so this code is the difference between telling the reader
  // the database did not answer and telling them their medicine was not created.
  //
  // UPSTREAM_FAILED is the BFF's answer for a 5xx whose body carried no code at
  // all: a dead channel, a cancelled call, a service that crashed mid-answer
  // (`grpcErrorToHttpException`). It reuses `api.error.server_error`, the key
  // ERR_BAD_RESPONSE already had, because it says the same thing to a reader; a
  // second key for one sentence would be the same defect as a second table.
  DATABASE_UNAVAILABLE: 'api.error.database_unavailable',
  UPSTREAM_FAILED: 'api.error.server_error',

  // Transport failures — the only entries here that no service throws.
  // `normalizeAxiosError` falls back to axios's own `error.code` whenever the
  // answer carried no domain code of its own, so these reach `apiErrorKey`
  // exactly like a domain code. Until they were listed, every one of them fell
  // to the caller's fallback, and a write that timed out was reported with the
  // same sentence as a write the server had refused outright. See
  // `isRequestUnresolved` for why that sentence was not merely vague but wrong.
  //
  // ERR_BAD_RESPONSE / ERR_BAD_REQUEST are axios's codes for an uncoded 5xx and
  // an uncoded 4xx. ERR_BAD_RESPONSE no longer answers a gRPC channel failure —
  // that arrives as UPSTREAM_FAILED now, and `normalizeAxiosError` prefers the
  // body's code over axios's — but it still answers a 5xx raised before any
  // service was reached: the BFF's own crash, or a proxy between the two.
  ECONNABORTED: 'api.error.request_timeout',
  ETIMEDOUT: 'api.error.request_timeout',
  ERR_NETWORK: 'api.error.request_unreachable',
  NETWORK_ERROR: 'api.error.request_unreachable',
  ERR_BAD_RESPONSE: 'api.error.server_error',
  ERR_BAD_REQUEST: 'api.error.request_rejected',

  // The codes the server now gives an error whose thrower named none (the catalogue's
  // generic ones, libs/shared/types error-catalogue.ts `statusErrorCode`). They say what
  // ERR_BAD_REQUEST / ERR_BAD_RESPONSE said before the server coded them, so each keeps
  // that key; a second sentence for the same refusal would be a second table.
  BAD_REQUEST: 'api.error.request_rejected',
  UNAUTHORIZED: 'api.error.request_rejected',
  NOT_FOUND: 'api.error.request_rejected',
  METHOD_NOT_ALLOWED: 'api.error.request_rejected',
  NOT_ACCEPTABLE: 'api.error.request_rejected',
  REQUEST_TIMEOUT: 'api.error.request_rejected',
  CONFLICT: 'api.error.request_rejected',
  PAYLOAD_TOO_LARGE: 'api.error.request_rejected',
  UNSUPPORTED_MEDIA_TYPE: 'api.error.request_rejected',
  UNPROCESSABLE_ENTITY: 'api.error.request_rejected',
  TOO_MANY_REQUESTS: 'api.error.request_rejected',
  INTERNAL_ERROR: 'api.error.server_error',
  BAD_GATEWAY: 'api.error.server_error',
  SERVICE_UNAVAILABLE: 'api.error.server_error',
  GATEWAY_TIMEOUT: 'api.error.server_error',

  // Correction decisions (UC50) — the keys the retired decisionErrorKey shipped
  EXPIRY_FORMAT: 'corrections.error.expiry_format',
  DECIDED_EXPIRY_UNCHANGED: 'corrections.error.expiry_unchanged',
  LOT_NO_POSITIVE_STOCK: 'corrections.error.lot_no_positive_stock',
  NOT_PENDING: 'corrections.error.not_pending',
  REJECTION_REASON_REQUIRED: 'corrections.error.reason_required',
  CORRECTION_WINDOW_EXPIRED: 'corrections.error.window_expired',
  // UC51: a request for this lot already waits for a decision (409). Reuses the
  // sentence the report page shows when it withholds the button for the same reason.
  ALREADY_PENDING: 'submission_detail.expiry_correction.already_pending',
};

// The Tolgee keys the table above references, for the i18n coverage test: a key
// missing from one of the three files is invisible until a reader hits that
// exact refusal. Exported as keys only, never as the mapping — a second reader
// of the mapping is how the second table grows back.
export const API_ERROR_TOLGEE_KEYS: readonly string[] = Object.freeze([
  ...new Set(Object.values(API_ERROR_KEYS)),
]);

// The refusal the administrator reads is ours, in their language, never the
// service's. Of the 252 coded throw sites, 146 send no human message at all —
// the API's `message` is then the bare code — and the 106 that do send one send
// English developer prose ("Medicine not found"). Neither belongs on screen, so
// this returns a Tolgee key and the server's own string is dropped.
//
// The fallback is the caller's, per operation: a delete that fails must not say
// that saving failed.
export function apiErrorKey(err: unknown, fallbackKey: string): string {
  const code = errorCodeOf(err);
  if (!code) return fallbackKey;
  return API_ERROR_KEYS[code] ?? fallbackKey;
}

// A refusal split into the two things a banner shows: which operation failed,
// and why. The screens that write more than one kind of thing need both — a
// refused observer-role creation headlined "saving failed" names the wrong
// operation, and so does a refused delete.
export type ApiRefusal = {
  // The operation the caller attempted, in the caller's own words. Always set,
  // because the reader is owed at least that much even when the service says
  // nothing we recognise.
  titleKey: string;
  // Why the service refused — null when it sent no code, or a code no screen
  // can act on, in which case `titleKey` is the whole message and the banner
  // must not print the same sentence twice.
  detailKey: string | null;
};

export function apiRefusal(err: unknown, operationKey: string): ApiRefusal {
  const key = apiErrorKey(err, operationKey);
  return { titleKey: operationKey, detailKey: key === operationKey ? null : key };
}

// The codes for which the request reached the server and the outcome of the
// write is unknown. The distinction matters to a writing screen, because the
// server may have carried the write out anyway: on 2026-09-01T07:34:50Z a
// medicine creation spent 46.5s inside catalog-service's `pg-pool.connect`, the
// browser gave up at its own 30s `timeout`, and the row was committed 9s later.
// The administrator was told the medicine had not been created. It had.
//
// The uncoded 5xx belongs here for a second reason: the create route is two
// independent gRPC calls and the medicine is committed before its first code is
// written (audit-system-admin-2026-08-30 M1), so a failure of the second one
// leaves the medicine behind.
//
// So a screen that writes must not headline any of these with "… could not be
// saved". It does not know that, and here it was not true.
const UNRESOLVED_CODES = new Set([
  'ECONNABORTED',
  'ETIMEDOUT',
  'ERR_NETWORK',
  'NETWORK_ERROR',
  'ERR_BAD_RESPONSE',
  // Both belong here for the same reason the axios codes do, and both had to be
  // added the moment the server started sending them: `normalizeAxiosError`
  // prefers the body's `code`, so a channel failure that used to reach this set
  // as ERR_BAD_RESPONSE now arrives as UPSTREAM_FAILED. Leaving them out would
  // have quietly put the create screen's "could not be created" headline back
  // up for exactly the failure that sentence was wrong about.
  //
  // DATABASE_UNAVAILABLE is unresolved even though the refused acquisition
  // itself executed nothing: a route that acquires a connection more than once
  // may have committed part of its work before one of them was refused, and the
  // medicine create is two gRPC calls with a code write after the medicine.
  'DATABASE_UNAVAILABLE',
  'UPSTREAM_FAILED',
  // The server's own codes for an uncoded 5xx, which ERR_BAD_RESPONSE was before the
  // server coded it: the same unknown outcome, the same reason to say nothing about the write.
  'INTERNAL_ERROR',
  'BAD_GATEWAY',
  'SERVICE_UNAVAILABLE',
  'GATEWAY_TIMEOUT',
]);

/**
 * True when the failure left the outcome of the write unknown — no answer at
 * all, or a server error mid-operation — rather than being a refusal the server
 * decided on and sent us.
 */
export function isRequestUnresolved(err: unknown): boolean {
  const code = errorCodeOf(err);
  return code !== undefined && UNRESOLVED_CODES.has(code);
}

/**
 * What a failed *read* may honestly be called.
 *
 * Only two answers are claims about the record itself: `not_found` says it is
 * not there, `forbidden` says the session may not have it. Everything else —
 * a refused connection, a service that is down, a channel that died, a session
 * that lapsed mid-flight — says nothing about the record at all, and is
 * `load_failed`.
 */
export type LoadFailureKind = 'not_found' | 'forbidden' | 'load_failed';

/**
 * Classify a failed read. This exists because six screens each answered the
 * question themselves and four of them answered it with `not_found` for *any*
 * error, so a 503 from a database that could not hand out a connection told the
 * administrator their medicine, alert rule, Registrar or storage site did not
 * exist. It is the read-path twin of `isRequestUnresolved`, which keeps the
 * write path from claiming a failure it does not know about either.
 *
 * The status is the only trustworthy discriminator here: the body's `code` is
 * per-service and the 28 codes no administration screen can reach are not in
 * `API_ERROR_KEYS` at all, so keying on it would send unknown refusals down the
 * `not_found` branch again. A status of 0 — no response — is `load_failed` by
 * the same rule.
 */
export function loadFailureKind(err: unknown): LoadFailureKind {
  const status = isNormalizedApiError(err)
    ? err.status
    : ((err as { response?: { status?: number } } | undefined)?.response?.status ?? 0);
  if (status === 404) return 'not_found';
  if (status === 403) return 'forbidden';
  return 'load_failed';
}

// The interceptor normalises everything it rejects, but a call that bypasses
// `apiClient` (or an error re-thrown by a caller) still arrives in the raw axios
// shape. Reading both means a code that exists is never lost to the fallback.
function errorCodeOf(err: unknown): string | undefined {
  if (isNormalizedApiError(err)) return err.code;
  if (typeof err === 'object' && err !== null) {
    const raw = (err as { response?: { data?: { code?: unknown } } }).response?.data?.code;
    if (typeof raw === 'string' && raw.length > 0) return raw;
  }
  return undefined;
}

function normalizeAxiosError(error: AxiosError): NormalizedApiError {
  const status = error.response?.status ?? 0;
  const responseData = error.response?.data as
    | {
        code?: string;
        message?: string;
        requestId?: string;
        draftId?: unknown;
        existingMedicineLabel?: unknown;
      }
    | undefined;
  const requestId =
    responseData?.requestId ?? (error.response?.headers['x-request-id'] as string | undefined);
  const draftId = responseData?.draftId;
  const existingMedicineLabel = responseData?.existingMedicineLabel;
  return {
    status,
    code: responseData?.code ?? error.code ?? 'NETWORK_ERROR',
    message: responseData?.message ?? 'Solicitarea nu a putut fi finalizată. Reîncercați.',
    ...(requestId ? { requestId } : {}),
    ...(typeof draftId === 'string' && draftId !== '' ? { draftId } : {}),
    ...(typeof existingMedicineLabel === 'string' && existingMedicineLabel !== ''
      ? { existingMedicineLabel }
      : {}),
  };
}

// What must outlive the page when a 401 sends the tab to sign in again. The
// redirect is a full navigation, so nothing unmounts and nothing in memory
// survives it; a flush cannot help either, since the session it would need is
// the one that is gone (docs/shape-ops-draft-session-handoff.md §D1).
const sessionRedirectHandlers = new Set<() => void>();

/** Runs `handler` synchronously right before the 401 redirect; returns its release. */
export function onSessionRedirect(handler: () => void): () => void {
  sessionRedirectHandlers.add(handler);
  return () => {
    sessionRedirectHandlers.delete(handler);
  };
}

export function createApiClient(): AxiosInstance {
  const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
  const client = axios.create({
    baseURL,
    withCredentials: true,
    // The ceiling every server-side wait is chosen against. Past this the
    // browser stops waiting and the screen has to say something without an
    // answer — which is how "the medicine could not be created" was printed
    // about one that had been. `DATABASE_CONNECTION_TIMEOUT_MS`
    // (libs/shared/database/src/lib/datasource-factory.ts) is set so that the
    // longest chain of refused acquisitions one request can make still lands
    // well inside this. Raising or lowering this number moves that one too.
    timeout: 30_000,
    headers: {
      Accept: 'application/json',
    },
  });

  // No call names a Registrar: the active one is the server session's, set only
  // by the menu's selection, and every tab of the session works on it (CF-27,
  // docs/shape-ops-tenant-selector.md §9).

  // caiet SEC-08: a request no input of the person's preceded does not keep the
  // session alive (docs/shape-ops-session-inactivity.md §4).
  client.interceptors.request.use((config) => {
    if (sentWithoutInput()) config.headers.set(SESSION_BACKGROUND_HEADER, '1');
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const normalized = normalizeAxiosError(error);
      if (normalized.status === 401) {
        const path = window.location.pathname;
        const onPublic =
          path === '/' ||
          path.startsWith('/auth/') ||
          path === '/dev-login' ||
          path === '/login-success';
        if (!onPublic) {
          // A 401 used to drop the reader on the landing page without a word,
          // which is how an expired session looked from the outside: the screen
          // they were reading simply became the front door
          // (audit-system-admin-2026-08-30 S5, FR-015). It now says so — but
          // only to a browser that has actually held a session. An address
          // opened or bookmarked without ever signing in is not an expiry, and
          // telling that reader otherwise would be a sentence we know to be
          // false (FR-014); the landing page is where they were going anyway.
          sessionRedirectHandlers.forEach((handler) => handler());
          window.location.assign(hasHeldSession() ? '/auth/session-expired' : '/');
        }
      }
      return Promise.reject(normalized);
    },
  );

  return client;
}

export const apiClient = createApiClient();
